import { http } from "../httpHandler";
import {
  Conversation,
  TServerErrorResponse,
  ChunkMetadata,
  UploadInitResponse,
  DirectUploadInitResponse,
  RefreshSasResponse,
  CompleteDirectUploadRequest,
  ChunkManifestResponse,
} from "@/types";
import { links } from "@/constants";
import { getAudioFileType } from "@/components/pages/Home/InPersonMeetingPage/helpers/supported-mime-type";

export const INCOMPLETE_RECORDINGS_GET = "incomplete-recordings-get";

export const initializeUpload = async (
  metadata: ChunkMetadata,
): Promise<UploadInitResponse> => {
  try {
    const requestBody = {
      totalChunks: metadata.totalChunks,
      filename: metadata.filename,
      mimeType: metadata.mimeType,
    } as const;

    const response = await http.post(links.record.recordInit, requestBody);
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};

export const uploadAudioChunk = async ({
  id,
  chunkIndex,
  chunk,
  metadata,
  checksum,
}: {
  chunk: Blob;
  chunkIndex: number;
  metadata: ChunkMetadata;
  checksum: string;
  id: string;
}) => {
  if (!id) {
    throw new Error("Upload session not initialized. Call startUpload first.");
  }
  const formData = new FormData();
  formData.append("chunk", chunk, `chunk${getAudioFileType()}`);

  const metadataToSend = {
    totalChunks: metadata.totalChunks,
    filename: metadata.filename,
    mimeType: metadata.mimeType,
  } as const;

  formData.append("metadata", JSON.stringify(metadataToSend));

  try {
    const response = await http.post(
      links.record.recordChunk(id, chunkIndex),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: { checksum },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};

export const completeChunkedUpload = async (
  uploadId: string,
  metadata: {
    title?: string;
    meetingId?: string;
    recordedAt?: number;
    checksum?: string;
  },
): Promise<Conversation> => {
  try {
    const completionData = {
      title: metadata.title || `Meeting on ${new Date().toLocaleDateString()}`,
      recordedAt: metadata.recordedAt || Date.now(),
      meetingId: metadata.meetingId,
    } as const;

    const response = await http.post(
      links.record.recordComplete(uploadId),
      completionData,
      {
        headers: {
          "Content-Type": "application/json",
        },
        params: { checksum: metadata.checksum },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};

export const pauseUploading = async (uploadId: string): Promise<number> => {
  try {
    const response = await http.post(links.record.pause(uploadId), {});
    return response.status;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

// full file upload as backup
export const createMeetingFromAudio = async (
  file: FormData,
  progressCallback?: (percentage: number) => void,
): Promise<Conversation> => {
  try {
    const response = await http.post(links.record.recordConversation, file, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (ev) => {
        if (progressCallback && ev.total) {
          progressCallback(Math.round((ev.loaded * 100) / ev.total));
        }
      },
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const deleteRecording = async (recordingId: string) => {
  try {
    const response = await http.delete(links.record.recordById(recordingId));
    return response.status;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getIncompleteRecordings = async (recordingKey: string | null) => {
  try {
    const url = recordingKey
      ? links.record.incomplete(recordingKey)
      : links.record.incompleteScan;
    const response = await http.get<{ uploadKey: string; directUpload?: boolean }>(url);
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const completeChunkedUploadPartial = async (
  uploadId: string,
  metadata: {
    title?: string;
    meetingId?: string;
    recordedAt?: number;
  },
): Promise<Conversation> => {
  try {
    const completionData = {
      title: metadata.title || `Meeting on ${new Date().toLocaleDateString()}`,
      recordedAt: metadata.recordedAt || Date.now(),
      meetingId: metadata.meetingId,
    } as const;

    const response = await http.post(
      links.record.recordCompletePartial(uploadId),
      completionData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};

export const initializeDirectUpload = async (
  metadata: ChunkMetadata,
): Promise<DirectUploadInitResponse> => {
  try {
    const requestBody = {
      totalChunks: metadata.totalChunks,
      filename: metadata.filename,
      mimeType: metadata.mimeType,
    } as const;

    const response = await http.post(
      links.record.recordInitDirect,
      requestBody,
    );
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};

export const refreshSasToken = async (
  uploadId: string,
): Promise<RefreshSasResponse> => {
  try {
    const response = await http.post(links.record.refreshSas(uploadId), {});
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};

export const CHUNK_MANIFEST_GET = "chunk-manifest-get";

export const getChunkManifest = async (
  uploadId: string,
): Promise<ChunkManifestResponse> => {
  try {
    const response = await http.get<ChunkManifestResponse>(
      links.record.chunkManifest(uploadId),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const completeDirectUpload = async (
  uploadId: string,
  metadata: CompleteDirectUploadRequest,
): Promise<Conversation> => {
  try {
    const response = await http.post(
      links.record.recordCompleteDirect(uploadId),
      metadata,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Full error:", error);
    throw error as TServerErrorResponse;
  }
};
